// Permisos por pantalla (catalogo.json es la unica fuente: tarjetas, editor y bloqueo de rutas).
//
// Un permiso es "<modulo>.<pantalla>.<accion>", p.ej. "ventas.pedidos.ver" o "ventas.pedidos.crear".
// El usuario los trae en user.permisos (viene en el token).
//
// Compatibilidad: los usuarios con token anterior a la migracion no traen `permisos`; para ellos
// todo se resuelve con los permisos booleanos viejos (legacy) que dice el catalogo.
import CATALOGO from "./catalogo.json";

export const MODULOS = CATALOGO.modulos;
export const RUTAS_BLOQUEADAS = CATALOGO.rutasBloqueadas;

const PANTALLAS = MODULOS.flatMap((m) => m.pantallas.map((p) => ({ ...p, modulo: m.key })));
const PANTALLA_POR_KEY = Object.fromEntries(PANTALLAS.map((p) => [p.key, p]));

const tieneNuevos = (user) => Array.isArray(user && user.permisos);
const algunLegacy = (user, flags) => (flags || []).some((f) => !!(user && user[f]));

// En el token los permisos vienen compactos: "ventas(pedidos:v,c|entregar:v);crm(...)".
// Mismo diccionario que compactaPermisos en EnvasesBHBack/utils/index.js.
const ACCIONES = { v: "ver", c: "crear", e: "editar", x: "cancelar", a: "autorizar", m: "mover", r: "recibir", s: "surtir", f: "frecuentes", p: "permisos" };
export function expandePermisos(compacto) {
  if (Array.isArray(compacto)) return compacto;
  if (typeof compacto !== "string") return undefined;
  const out = [];
  compacto.split(";").forEach((bloque) => {
    const m = bloque.match(/^([^(]+)\((.*)\)$/);
    if (!m) return;
    m[2].split("|").forEach((pa) => {
      const [pant, accs] = pa.split(":");
      (accs || "").split(",").filter(Boolean).forEach((a) => out.push(`${m[1]}.${pant}.${ACCIONES[a] || a}`));
    });
  });
  return out;
}

// Usuario tal como lo usa el front: permisos expandidos + booleanos viejos recalculados.
export function preparaUsuario(decoded) {
  const permisos = expandePermisos(decoded && decoded.permisos);
  const u = permisos ? { ...decoded, permisos } : { ...decoded };
  if (!permisos) delete u.permisos;
  return conLegacyDerivado(u);
}

// ¿El usuario puede hacer `clave`? (p.ej. "ventas.pedidos.crear"; sin accion = "ver")
export function puede(user, clave) {
  if (!user) return false;
  const partes = clave.split(".");
  const accion = partes.length > 2 ? partes.pop() : "ver";
  const pantalla = PANTALLA_POR_KEY[partes.join(".")];
  if (tieneNuevos(user)) return user.permisos.includes(`${partes.join(".")}.${accion}`);
  if (!pantalla) return false;
  // Token viejo: se resuelve con los booleanos de antes.
  if (!algunLegacy(user, pantalla.legacyVer)) return false;
  if (accion === "ver") return true;
  const a = pantalla.acciones.find((x) => x.key === accion);
  if (!a) return false;
  return a.legacy.length ? algunLegacy(user, a.legacy) : true;
}

// ¿Ve al menos una pantalla del modulo? (para el menu lateral)
export function puedeModulo(user, moduloKey) {
  const m = MODULOS.find((x) => x.key === moduloKey);
  return !!m && m.pantallas.some((p) => puede(user, `${p.key}.ver`));
}

// Que pantalla/accion corresponde a una ruta. null = ruta libre (no esta en el catalogo).
export function permisoDeRuta(ruta) {
  const r = (ruta || "").replace(/\/+$/, "") || "/";
  const igual = (x) => x.toLowerCase() === r.toLowerCase();
  for (const p of PANTALLAS) {
    if (p.rutas.some(igual)) return `${p.key}.ver`;
    for (const a of p.acciones) if (a.rutas.some(igual)) return `${p.key}.${a.key}`;
  }
  return null;
}

export const rutaBloqueada = (ruta) =>
  RUTAS_BLOQUEADAS.some((b) => b.toLowerCase() === (ruta || "").replace(/\/+$/, "").toLowerCase());

// Convierte los booleanos viejos de un usuario a la lista nueva (lo que ve hoy, tal cual).
// Una accion sin booleano viejo se concede si ya veia la pantalla (hoy no estaba restringida).
export function permisosDesdeLegacy(u) {
  const out = [];
  PANTALLAS.forEach((p) => {
    if (!algunLegacy(u, p.legacyVer)) return;
    out.push(`${p.key}.ver`);
    p.acciones.forEach((a) => {
      if (!a.legacy.length || algunLegacy(u, a.legacy)) out.push(`${p.key}.${a.key}`);
    });
  });
  return out;
}

// Al revés: con los permisos nuevos se recalculan los booleanos viejos que el catalogo controla,
// para que las pantallas que aun revisan user.menu_x / user.x_create sigan funcionando igual.
export function conLegacyDerivado(user) {
  if (!tieneNuevos(user)) return user;
  const u = { ...user };
  const tiene = (k) => user.permisos.includes(k);
  // Primero se apagan todos los booleanos que controla el catalogo...
  MODULOS.forEach((m) => (m.legacyModulo || []).forEach((f) => (u[f] = false)));
  PANTALLAS.forEach((p) => {
    [...p.legacyVer, ...p.compatVer].forEach((f) => (u[f] = false));
    p.acciones.forEach((a) => a.legacy.forEach((f) => (u[f] = false)));
  });
  // ...y se prenden los que corresponden a lo concedido.
  MODULOS.forEach((m) => {
    if (m.pantallas.some((p) => tiene(`${p.key}.ver`))) (m.legacyModulo || []).forEach((f) => (u[f] = true));
  });
  PANTALLAS.forEach((p) => {
    if (!tiene(`${p.key}.ver`)) return;
    [...p.legacyVer, ...p.compatVer].forEach((f) => (u[f] = true));
    p.acciones.forEach((a) => {
      if (tiene(`${p.key}.${a.key}`)) a.legacy.forEach((f) => (u[f] = true));
    });
  });
  return u;
}
